import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  getUserAnalyticsApi,
  getAnalyticsLogByIdApi,
} from "../../services/analytics";
import useFullPageLoader from "../../hooks/useFullPageLoader";
import Pagination from "react-js-pagination";
import { toastMessage } from "../../utils/toastMessage";
import { dateFormatUtc, dateFormat } from "../../utils/dateFormat";
import {
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Box,
  Grid,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { SecondaryButton } from "../../components/Button/Button";
import IconButtonComponent from "../../components/Button/IconButton";
import scrollToUp from "../../utils/scrollToUp";
import Loader from "../../comman/Loader";
import NotFound from "../../components/not-found/NotFound";
import findSerialNumber from "../../utils/findSerialNumber";

const UserAnalytics = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [loader, showLoader, hideLoader] = useFullPageLoader();

  // Pagination
  const [activePage, setActivePage] = useState(1);
  const [pageLimit, setPageLimit] = useState(50);

  // Data states
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  // Detail modal
  const [detailModal, setDetailModal] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Page limit options
  const pageLimitOptions = [
    { value: 10, label: "10 per page" },
    { value: 20, label: "20 per page" },
    { value: 25, label: "25 per page" },
    { value: 50, label: "50 per page" },
    { value: 100, label: "100 per page" },
  ];

  // Fetch logs for user
  const fetchUserLogs = async (page = 1) => {
    setLoading(true);
    try {
      const params = {
        page: page,
        limit: pageLimit,
      };

      const response = await getUserAnalyticsApi(userId, params);
      if (response.success) {
        setLogs(response.data.logs || []);
        setTotal(response.data.pagination?.total || 0);
      } else {
        toastMessage("error", response.message || "Failed to fetch user logs");
      }
    } catch (error) {
      toastMessage("error", error.message || "Failed to fetch user logs");
    } finally {
      setLoading(false);
    }
  };

  // View log details
  const handleViewDetails = async (logId) => {
    setLoadingDetail(true);
    setDetailModal(true);
    try {
      const response = await getAnalyticsLogByIdApi(logId);
      if (response.success) {
        setSelectedLog(response.data.log);
      } else {
        toastMessage("error", response.message || "Failed to fetch log details");
        setDetailModal(false);
      }
    } catch (error) {
      toastMessage("error", error.message || "Failed to fetch log details");
      setDetailModal(false);
    } finally {
      setLoadingDetail(false);
    }
  };

  // Close detail modal
  const closeDetailModal = () => {
    setDetailModal(false);
    setSelectedLog(null);
  };

  useEffect(() => {
    scrollToUp();
    fetchUserLogs(activePage);
  }, [activePage, pageLimit, userId]);

  return (
    <>
      {loader}
      <div className="WrapperArea">
        <div className="WrapperBox">
          <div className="TitleBox">
            <div className="user_detail_heading">
              <SecondaryButton
                onClick={() => navigate("/analytics-management")}
                style={{ marginRight: "15px" }}
              >
                ← Back to Analytics
              </SecondaryButton>
              <h4 className="Title">User Analytics - {userId}</h4>
            </div>
          </div>

          {/* Logs Table */}
          <div className="content-table">
            {loading ? (
              <div className="text-center py-5">
                <Loader />
              </div>
            ) : logs.length > 0 ? (
              <>
                <div style={{ marginBottom: "20px" }}>
                  <Typography variant="body1" color="textSecondary">
                    Total Logs: {total}
                  </Typography>
                </div>
                <table>
                  <thead>
                    <tr>
                      <th>S. No.</th>
                      <th>Event Name</th>
                      <th>Event Type</th>
                      <th>Source</th>
                      <th>Platform</th>
                      <th>Device Type</th>
                      <th>Device Model</th>
                      <th>App Version</th>
                      <th>Created At</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log, index) => (
                      <tr key={log._id || index}>
                        <td>{findSerialNumber(index, activePage, pageLimit)}</td>
                        <td>{log.event_name || "---"}</td>
                        <td>
                          <span
                            className="badge"
                            style={{
                              backgroundColor: "#28B17B",
                              color: "white",
                              padding: "4px 8px",
                              borderRadius: "4px",
                              fontSize: "12px",
                            }}
                          >
                            {log.event_type || "---"}
                          </span>
                        </td>
                        <td>
                          {log.source ? (
                            <span
                              className="badge"
                              style={{
                                backgroundColor: "#FF8989",
                                color: "white",
                                padding: "4px 8px",
                                borderRadius: "4px",
                                fontSize: "12px",
                              }}
                            >
                              {log.source}
                            </span>
                          ) : (
                            "---"
                          )}
                        </td>
                        <td>{log.platform || "---"}</td>
                        <td>
                          {log.device_info?.device_type ? (
                            <span
                              className="badge"
                              style={{
                                backgroundColor: log.device_info.device_type === "ios" ? "#007AFF" : log.device_info.device_type === "android" ? "#3DDC84" : "#999",
                                color: "white",
                                padding: "4px 8px",
                                borderRadius: "4px",
                                fontSize: "12px",
                                textTransform: "uppercase",
                              }}
                            >
                              {log.device_info.device_type}
                            </span>
                          ) : (
                            "---"
                          )}
                        </td>
                        <td>{log.device_info?.device_model || "---"}</td>
                        <td>{log.device_info?.app_version || "---"}</td>
                     
                        <td>{log.createdAt ? dateFormat(log.createdAt) : "---"}</td>
                        <td>
                        <IconButton onClick={() => handleViewDetails(log._id)}>
                                  <VisibilityIcon className="eye_icon" />
                                </IconButton> 
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {total > pageLimit && (
                  <div style={{ marginTop: "20px", display: "flex", justifyContent: "center" }}>
                    <Pagination
                      activePage={activePage}
                      itemsCountPerPage={pageLimit}
                      totalItemsCount={total}
                      pageRangeDisplayed={5}
                      onChange={(page) => {
                        setActivePage(page);
                      }}
                      itemClass="page-item"
                      linkClass="page-link"
                    />
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-5">
                <NotFound message="No logs found for this user!" />
              </div>
            )}
          </div>

          {/* Pagination Controls */}
          <div className="pagination-wrapper mt-4 d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center gap-3">
              <Typography variant="body2" color="textSecondary">
                Show:
              </Typography>
              <select
                value={pageLimit}
                onChange={(e) => {
                  setPageLimit(parseInt(e.target.value));
                  setActivePage(1);
                }}
                style={{
                  padding: "8px 12px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  fontSize: "14px",
                  minWidth: "80px",
                }}
              >
                {pageLimitOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.value}
                  </option>
                ))}
              </select>
              <Typography variant="body2" color="textSecondary">
                per page
              </Typography>
            </div>
            <div className="d-flex align-items-center gap-2">
              <Typography variant="body2" color="textSecondary">
                Showing {((activePage - 1) * pageLimit) + 1} to{" "}
                {Math.min(activePage * pageLimit, total)} of {total} items
              </Typography>
            </div>
          </div>
        </div>
      </div>

      {/* Detail Modal - Reuse from AnalyticsManagement */}
      <Dialog
        open={detailModal}
        onClose={closeDetailModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6">Analytics Log Details</Typography>
            <IconButton onClick={closeDetailModal}>
              <CloseIcon />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent>
          {loadingDetail ? (
            <div className="text-center py-5">
              <Loader />
            </div>
          ) : selectedLog ? (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Event Name
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {selectedLog.event_name || "---"}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Event Type
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {selectedLog.event_type || "---"}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Source
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {selectedLog.source || "---"}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    User ID
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2, wordBreak: "break-word" }}>
                    {selectedLog.user_id || "---"}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Platform
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {selectedLog.platform || "---"}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Created At
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {selectedLog.createdAt
                      ? dateFormat(new Date(selectedLog.createdAt))
                      : "---"}
                  </Typography>
                </Grid>
                {selectedLog.device_info && (
                  <>
                    <Grid item xs={12}>
                      <Typography
                        variant="h6"
                        sx={{
                          mt: 2,
                          mb: 2,
                          pb: 1,
                          borderBottom: "2px solid #e0e0e0",
                          fontWeight: "bold",
                        }}
                      >
                        Device Information
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Device ID
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 2, wordBreak: "break-word" }}>
                        {selectedLog.device_info.device_id || "---"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Device Type
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        {selectedLog.device_info.device_type || "---"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Device Model
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        {selectedLog.device_info.device_model || "---"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="textSecondary">
                        App Version
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        {selectedLog.device_info.app_version || "---"}
                      </Typography>
                    </Grid>
                  </>
                )}
                {selectedLog.event_params &&
                  Object.keys(selectedLog.event_params).length > 0 && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Event Parameters
                      </Typography>
                      <Box
                        sx={{
                          mt: 1,
                          p: 2,
                          bgcolor: "#f5f5f5",
                          borderRadius: 1,
                          maxHeight: "300px",
                          overflow: "auto",
                        }}
                      >
                        <pre style={{ margin: 0, fontSize: "12px" }}>
                          {JSON.stringify(selectedLog.event_params, null, 2)}
                        </pre>
                      </Box>
                    </Grid>
                  )}
              </Grid>
            </Box>
          ) : (
            <Typography className="text-center py-5">
              No details available
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <SecondaryButton onClick={closeDetailModal}>Close</SecondaryButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UserAnalytics;

